import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import { useNavigate, useParams } from "react-router-dom";
import Sidebar from "../AdminPanel/Sidebar";
import TopBar from "../AdminPanel/TopBar";
import { useDashboardTheme } from "../../../context/DashboardThemeContext";
import ProductForm from "./Products/ProductForm";
import ProductImages from "./Products/ProductImages";

export default function ProductDetail() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { dashboardTheme } = useDashboardTheme();
  const navigate = useNavigate();
  const { productId } = useParams();

  const [loadingProduct, setLoadingProduct] = useState(true);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const [form, setForm] = useState({
    name: "",
    ecommerce: true,
    stock_minimo: "",
    pvp1: "",
    iva_porcentaje: 15,
    category: null,
    brand: null,
    barcode: "",
    codigo_auxiliar: "",
    estado: "activo",
    description: "",
  });

  const [images, setImages] = useState([]);
  const [nextCode, setNextCode] = useState("");

  const [statusModal, setStatusModal] = useState({
    open: false,
    type: "loading",
    message: "",
  });

  const token = localStorage.getItem("accessToken");

  const outlinedButtonSx = {
    minHeight: 40,
    px: 2.4,
    borderRadius: "12px",
    textTransform: "none",
    fontWeight: 700,
    color: dashboardTheme.button_color,
    borderColor: dashboardTheme.button_color,
    boxShadow: "none",
    "&:hover": {
      borderColor: dashboardTheme.button_color,
      bgcolor: `${dashboardTheme.button_color}12`,
      boxShadow: "none",
    },
  };

  const dangerButtonSx = {
    minHeight: 40,
    px: 2.4,
    borderRadius: "12px",
    textTransform: "none",
    fontWeight: 700,
    color: "#dc2626",
    borderColor: "#dc2626",
    boxShadow: "none",
    "&:hover": {
      borderColor: "#dc2626",
      bgcolor: "rgba(220,38,38,0.08)",
      boxShadow: "none",
    },
  };

  const loadProduct = async () => {
    try {
      setLoadingProduct(true);

      const response = await fetch(`/api/products/products/${productId}/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        setStatusModal({
          open: true,
          type: "error",
          message: data.error || data.detail || "No se pudo cargar el producto.",
        });
        return;
      }

      const product = data.producto;

      setNextCode(product.code || "");

      setForm({
        name: product.name || "",
        ecommerce: !!product.ecommerce,
        stock_minimo: product.stock_minimo ?? "",
        pvp1: product.pvp1 ?? "",
        iva_porcentaje: product.iva_porcentaje ?? 15,
        category: product.category || null,
        brand: product.brand || null,
        barcode: product.barcode || "",
        codigo_auxiliar: product.codigo_auxiliar || "",
        estado: product.estado || "activo",
        description: product.description || "",
      });

      setImages(
        (product.images || []).map((image) => ({
          id: image.id,
          backendId: image.id,
          image: image.image,
          preview: image.image,
          name: `Imagen ${image.id}`,
          existing: true,
        }))
      );
    } catch {
      setStatusModal({
        open: true,
        type: "error",
        message: "Error de conexión.",
      });
    } finally {
      setLoadingProduct(false);
    }
  };

  useEffect(() => {
    loadProduct();
  }, [productId]);

  const showErrorModal = (message) => {
    setStatusModal({
      open: true,
      type: "error",
      message,
    });
  };

  const closeStatusModal = () => {
    if (statusModal.type === "loading") return;
    setStatusModal((prev) => ({ ...prev, open: false }));
  };

  const validateForm = () => {
    if (!form.name.trim()) {
      showErrorModal("El nombre del producto es obligatorio.");
      return false;
    }

    if (form.pvp1 === "" || Number(form.pvp1) < 0) {
      showErrorModal("El PVP debe ser mayor o igual a cero.");
      return false;
    }

    if (form.stock_minimo !== "" && Number(form.stock_minimo) < 0) {
      showErrorModal("El stock mínimo no puede ser negativo.");
      return false;
    }

    return true;
  };

  const parseBackendError = (data) => {
    const error = data?.error || data?.detail;

    if (!error) {
      return "No se pudo procesar el producto.";
    }

    if (typeof error === "string") {
      return error;
    }

    if (typeof error === "object") {
      return Object.values(error).flat().join(" ");
    }

    return "No se pudo procesar el producto.";
  };

  const saveProduct = async () => {
    if (!validateForm()) return;

    try {
      setStatusModal({
        open: true,
        type: "loading",
        message: "Guardando cambios...",
      });

      const fd = new FormData();

      fd.append("name", form.name.trim());
      fd.append("ecommerce", form.ecommerce ? "true" : "false");
      fd.append("stock_minimo", form.stock_minimo === "" ? "" : form.stock_minimo);
      fd.append("pvp1", form.pvp1);
      fd.append("iva_porcentaje", form.iva_porcentaje);
      fd.append("aplica_ice", "false");
      fd.append("ice_porcentaje", "0");
      fd.append("category_id", form.category?.id || "");
      fd.append("brand_id", form.brand?.id || "");
      fd.append("barcode", form.barcode.trim());
      fd.append("codigo_auxiliar", form.codigo_auxiliar.trim());
      fd.append("estado", form.estado);
      fd.append("description", form.description.trim());

      images
        .filter((item) => item.file && !item.existing)
        .forEach((item) => {
          fd.append("images", item.file);
        });

      const response = await fetch(`/api/products/products/${productId}/`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: fd,
      });

      const data = await response.json();

      if (!response.ok) {
        setStatusModal({
          open: true,
          type: "error",
          message: parseBackendError(data),
        });
        return;
      }

      setStatusModal({
        open: true,
        type: "success",
        message: data.mensaje || "Producto actualizado correctamente.",
      });

      await loadProduct();
    } catch {
      setStatusModal({
        open: true,
        type: "error",
        message: "Error de conexión.",
      });
    }
  };

  const deleteProduct = async () => {
    try {
      setDeleteOpen(false);

      setStatusModal({
        open: true,
        type: "loading",
        message: "Eliminando producto...",
      });

      const response = await fetch(`/api/products/products/${productId}/delete/`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        setStatusModal({
          open: true,
          type: "error",
          message: data.error || data.detail || "No se pudo eliminar el producto.",
        });
        return;
      }

      setStatusModal({
        open: true,
        type: "success",
        message: data.mensaje || "Producto eliminado correctamente.",
      });

      setTimeout(() => {
        navigate("/admin-panel/productos");
      }, 1200);
    } catch {
      setStatusModal({
        open: true,
        type: "error",
        message: "Error de conexión.",
      });
    }
  };

  const deleteExistingImage = async (image) => {
    if (!image?.backendId) return;

    try {
      const response = await fetch(`/api/products/products/images/${image.backendId}/delete/`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        showErrorModal(data.error || data.detail || "No se pudo eliminar la imagen.");
        return;
      }

      setImages((prev) => prev.filter((item) => item.id !== image.id));
    } catch {
      showErrorModal("Error de conexión.");
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: dashboardTheme.dashboard_background,
        display: "flex",
        color: dashboardTheme.text_color,
        overflowX: "hidden",
      }}
    >
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <TopBar
          sidebarOpen={sidebarOpen}
          onToggleSidebar={() => setSidebarOpen((prev) => !prev)}
        />

        <Box
          component="main"
          sx={{
            flex: 1,
            width: "100%",
            p: { xs: 1.5, sm: 2, md: 3 },
          }}
        >
          <Box
            sx={{
              width: { xs: "100%", lg: "95%" },
              mx: "auto",
              mt: { xs: 1, md: 1.5 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: { xs: "stretch", sm: "center" },
                justifyContent: "space-between",
                flexDirection: { xs: "column", sm: "row" },
                gap: 1.5,
                mb: { xs: 1.5, md: 2 },
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: 24, sm: 26, md: 30 },
                  fontWeight: 600,
                  color: dashboardTheme.primary_color,
                  lineHeight: 1.2,
                }}
              >
                Editar Producto
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 1,
                }}
              >
                <Button
                  variant="outlined"
                  startIcon={<DeleteOutlineRoundedIcon />}
                  onClick={() => setDeleteOpen(true)}
                  sx={dangerButtonSx}
                >
                  Eliminar
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<ArrowBackRoundedIcon />}
                  onClick={() => navigate("/admin-panel/productos")}
                  sx={outlinedButtonSx}
                >
                  Regresar
                </Button>
              </Box>
            </Box>

            <Paper
              elevation={0}
              sx={{
                borderRadius: { xs: "10px", md: "12px" },
                border: `1px solid ${dashboardTheme.border_color}`,
                bgcolor: dashboardTheme.card_background,
                p: { xs: 1.5, sm: 2, md: 2.5 },
              }}
            >
              {loadingProduct ? (
                <Box
                  sx={{
                    minHeight: 260,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    gap: 1.5,
                  }}
                >
                  <CircularProgress size={42} sx={{ color: dashboardTheme.primary_color }} />
                  <Typography sx={{ color: dashboardTheme.text_color, fontWeight: 600 }}>
                    Cargando producto...
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={3}>
                  <ProductForm
                    form={form}
                    setForm={setForm}
                    nextCode={nextCode}
                    onSubmit={saveProduct}
                  />

                  <ProductImages
                    images={images}
                    setImages={setImages}
                    onDeleteExistingImage={deleteExistingImage}
                  />
                </Stack>
              )}
            </Paper>
          </Box>
        </Box>
      </Box>

      <Dialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: "16px",
            bgcolor: dashboardTheme.card_background,
            border: `1px solid ${dashboardTheme.border_color}`,
            boxShadow: "none",
          },
        }}
      >
        <DialogTitle
          sx={{
            color: dashboardTheme.text_color,
            fontWeight: 600,
            fontSize: 20,
          }}
        >
          Eliminar producto
        </DialogTitle>

        <DialogContent>
          <Typography sx={{ color: dashboardTheme.text_color, fontSize: 15 }}>
            Si el producto tiene historial relacionado, el sistema lo pondrá como inactivo.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setDeleteOpen(false)}
            sx={outlinedButtonSx}
          >
            Cancelar
          </Button>

          <Button
            variant="outlined"
            onClick={deleteProduct}
            sx={dangerButtonSx}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={statusModal.open}
        onClose={closeStatusModal}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: "16px",
            bgcolor: dashboardTheme.card_background,
            border: `1px solid ${dashboardTheme.border_color}`,
            boxShadow: "none",
          },
        }}
      >
        <DialogContent
          sx={{
            py: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            gap: 1.5,
          }}
        >
          {statusModal.type === "loading" && (
            <CircularProgress size={42} sx={{ color: dashboardTheme.primary_color }} />
          )}

          {statusModal.type === "success" && (
            <CheckCircleRoundedIcon sx={{ fontSize: 48, color: "#2E7D32" }} />
          )}

          {statusModal.type === "error" && (
            <ErrorOutlineRoundedIcon sx={{ fontSize: 48, color: "#D32F2F" }} />
          )}

          <Typography
            sx={{
              color:
                statusModal.type === "error"
                  ? "#D32F2F"
                  : statusModal.type === "success"
                  ? "#2E7D32"
                  : dashboardTheme.text_color,
              fontSize: 18,
              fontWeight: 600,
            }}
          >
            {statusModal.message}
          </Typography>

          {statusModal.type === "error" && (
            <Button
              variant="contained"
              onClick={closeStatusModal}
              sx={dashboardTheme.buttonStyles.contained}
            >
              Entendido
            </Button>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}